import { ArrowLeft, CalendarDays, CircleAlert, Contact, ExternalLink, ListChecks, Tag } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { formatDate, getActiveNews, getArchivedNews, getDepartmentName } from "../lib/content";
import { NotFoundPage } from "./NotFoundPage";
import { lifecycleState } from "../lib/lifecycle";

export function NewsDetailPage() {
  const { slug } = useParams();
  const now = new Date();
  const item = [...getActiveNews(now), ...getArchivedNews(now)].find((entry) => entry.slug === slug);
  if (!item) return <NotFoundPage />;
  const relatedLinks = item.resourceLinks.filter(
    (link): link is { label: string; url: string } => Boolean(link.url),
  );

  return (
    <article className="page-width article-page">
      <nav aria-label="Breadcrumb" className="breadcrumb">
        <Link to="/news"><ArrowLeft aria-hidden="true" size={17} /> All updates</Link>
        <span>/</span>
        <Link to={`/departments/${item.department}`}>{getDepartmentName(item.department)}</Link>
      </nav>
      <header className="article-header">
        <span className={`priority-label priority-${item.priority}`}>{item.priority === "standard" ? item.category : `${item.priority} priority`}</span>
        <h1>{item.title}</h1>
        <p>{item.summary}</p>
        <div className="article-byline">
          <span>Published {formatDate(item.publishedAt)}</span>
          <span>{getDepartmentName(item.department)}</span>
          {lifecycleState(item) === "archived" ? <span>Archive / history — no longer current</span> : null}
        </div>
      </header>
      <div className="article-layout">
        <div className="article-body">
          {item.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {relatedLinks.length ? (
            <section aria-labelledby="related-links-heading" className="related-links">
              <h2 id="related-links-heading">Related links</h2>
              <ul>
                {relatedLinks.map((link) => (
                  <li key={`${link.label}-${link.url}`}>
                    <a href={link.url} rel="noopener noreferrer" target="_blank">
                      {link.label} <ExternalLink aria-hidden="true" size={17} />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
        <aside aria-label="Update details" className="article-facts">
          <h2>Update details</h2>
          <dl>
            <div>
              <dt><ListChecks aria-hidden="true" /> What changed</dt>
              <dd>{item.summary}</dd>
            </div>
            <div>
              <dt><CalendarDays aria-hidden="true" /> Effective date</dt>
              <dd>{item.effectiveAt ? formatDate(item.effectiveAt) : "No separate effective date"}</dd>
            </div>
            <div className={item.actionNeeded ? "fact-action" : undefined}>
              <dt><CircleAlert aria-hidden="true" /> Action needed</dt>
              <dd>{item.actionText ?? "No action is required."}</dd>
            </div>
            <div>
              <dt><Contact aria-hidden="true" /> Contact / owner</dt>
              <dd>{item.contact}</dd>
            </div>
            <div>
              <dt><Tag aria-hidden="true" /> Category</dt>
              <dd>{item.category}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </article>
  );
}
